import type { MembershipInformation } from '@chewy/loyalty-common/types';

import { ANNUAL_2024_PLAN_NAME } from '@/constants/plans';
import {
  LoyaltyAvailableMembershipPlans,
  LoyaltyMembershipPlanTrajectory,
  LoyaltyPlanType,
} from '@/services/graphql/__generated__';

export const getOfferedMembershipPlan = (
  membershipPlans?: LoyaltyAvailableMembershipPlans | null,
): LoyaltyMembershipPlanTrajectory | undefined => membershipPlans?.plans?.[0];

export const isFreeTrialMembershipPlan = (
  membershipPlan?: LoyaltyMembershipPlanTrajectory | null,
): boolean => membershipPlan?.planType === LoyaltyPlanType.FreeTrial;

export const getMembershipPlanPrice = (
  membershipPlan?: LoyaltyMembershipPlanTrajectory | null,
): number | undefined =>
  isFreeTrialMembershipPlan(membershipPlan)
    ? membershipPlan?.nextPrice?.amount
    : membershipPlan?.price?.amount;

export const getMembershipPlanIntroductoryPrice = (
  membershipPlan?: LoyaltyMembershipPlanTrajectory | null,
): boolean =>
  isFreeTrialMembershipPlan(membershipPlan)
    ? membershipPlan?.nextPlan === ANNUAL_2024_PLAN_NAME
    : membershipPlan?.plan === ANNUAL_2024_PLAN_NAME;

export const getMembershipInformationFromPlan = (
  membershipPlan?: LoyaltyMembershipPlanTrajectory | null,
): MembershipInformation | null => {
  if (!membershipPlan) return null;

  const isFreeTrial = isFreeTrialMembershipPlan(membershipPlan);
  const planPrice = getMembershipPlanPrice(membershipPlan);
  const freeTrialDuration =
    isFreeTrial && membershipPlan.durationInDays != null
      ? String(membershipPlan.durationInDays)
      : undefined;
  const nextRenewalDate = membershipPlan.nextChargedTimestamp
    ? new Date(membershipPlan.nextChargedTimestamp)
    : undefined;

  if (
    planPrice == null ||
    nextRenewalDate == null ||
    Number.isNaN(nextRenewalDate.getTime()) ||
    (isFreeTrial && freeTrialDuration == null)
  ) {
    return null;
  }

  return {
    plan: membershipPlan.plan ?? undefined,
    planPrice,
    freeTrialDuration,
    nextRenewalDate,
  };
};
